// Offline sync engine: replay the outbox against Supabase, and reconstruct the
// in-memory event list as "server truth + still-pending outbox on top."

import { supabase } from "../supabase";
import { EVENT_COLUMNS, type EventRow } from "../events";
import { deleteOutbox, getOutbox, type StoredOutbox } from "./db";

/**
 * Did this fail because we're offline (retry later) or because the server
 * rejected it (give up)? `navigator.onLine === false` is reliable; an online
 * failure with a fetch-shaped message is treated as a network error too, so a
 * stale `onLine` flag never costs a logged feed.
 */
export function isNetworkError(error: { message?: string } | null | undefined): boolean {
  if (!navigator.onLine) return true;
  const msg = (error?.message ?? "").toLowerCase();
  return msg.includes("fetch") || msg.includes("network") || msg.includes("failed to send");
}

// Insert payload = the row minus the server-owned columns.
function insertPayload(row: EventRow): Record<string, unknown> {
  const { id: _id, created_at: _createdAt, ...rest } = row;
  void _id;
  void _createdAt;
  return rest;
}

/**
 * Replay every queued mutation for `babyId`, oldest first. A synced insert calls
 * `onInsertSynced(tmpId, realRow)` so the caller can swap the optimistic row.
 * Stops (leaving the rest queued) the moment a network error reappears.
 */
export async function flushOutbox(
  babyId: string,
  onInsertSynced: (tmpId: string, realRow: EventRow) => void,
): Promise<void> {
  if (!navigator.onLine) return;

  const entries = await getOutbox(babyId);
  const idMap = new Map<string, string>(); // tmp localId -> real server id

  for (const entry of entries) {
    try {
      if (entry.kind === "insert") {
        const { data, error } = await supabase
          .from("events")
          .insert(insertPayload(entry.row))
          .select(EVENT_COLUMNS)
          .single();
        if (error || !data) {
          if (isNetworkError(error)) return; // still offline — try again later
          await deleteOutbox(entry.seq); // hard rejection — can never succeed
          continue;
        }
        const realRow = data as EventRow;
        idMap.set(entry.localId, realRow.id);
        onInsertSynced(entry.localId, realRow);
        await deleteOutbox(entry.seq);
      } else if (entry.kind === "update") {
        const targetId = idMap.get(entry.targetId) ?? entry.targetId;
        const { error } = await supabase.from("events").update(entry.patch).eq("id", targetId);
        if (error && isNetworkError(error)) return;
        await deleteOutbox(entry.seq);
      } else {
        const targetId = idMap.get(entry.targetId) ?? entry.targetId;
        const { error } = await supabase.from("events").delete().eq("id", targetId);
        if (error && isNetworkError(error)) return;
        await deleteOutbox(entry.seq);
      }
    } catch {
      return; // a thrown fetch error — network down, retry later
    }
  }
}

/** Server truth + still-pending outbox replayed on top → what to render. */
export function applyOutbox(serverEvents: EventRow[], pending: StoredOutbox[]): EventRow[] {
  let evs = [...serverEvents];
  for (const e of pending) {
    if (e.kind === "insert") {
      evs = [e.row, ...evs.filter((x) => x.id !== e.row.id)];
    } else if (e.kind === "update") {
      evs = evs.map((x) => (x.id === e.targetId ? { ...x, ...e.patch } : x));
    } else {
      evs = evs.filter((x) => x.id !== e.targetId);
    }
  }
  return evs.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}
